export function normalizeWorker360Value(value) {
  return String(value || "").trim().toLowerCase();
}

export function worker360ToDate(value) {
  if (!value) return null;

  if (typeof value?.toDate === "function") {
    return value.toDate();
  }

  if (typeof value?.seconds === "number") {
    return new Date(value.seconds * 1000);
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
}

export function getWorker360ShiftStart(shift) {
  return worker360ToDate(
    shift?.startTime ||
      shift?.startAt ||
      shift?.startDateTime ||
      shift?.shiftStart ||
      shift?.date
  );
}

export function getWorker360ShiftEnd(shift) {
  return worker360ToDate(
    shift?.endTime ||
      shift?.endAt ||
      shift?.endDateTime ||
      shift?.shiftEnd
  );
}

export function getWorker360ShiftHours(shift) {
  const storedHours = Number(
    shift?.hoursWorked ??
      shift?.totalHours ??
      shift?.hours ??
      shift?.durationHours
  );

  if (Number.isFinite(storedHours) && storedHours > 0) {
    return storedHours;
  }

  const start = getWorker360ShiftStart(shift);
  const end = getWorker360ShiftEnd(shift);

  if (!start || !end) return 0;

  const hours =
    (end.getTime() - start.getTime()) / 3600000;

  return hours > 0 ? hours : 0;
}

export function shiftBelongsToWorker(shift, workerId) {
  const normalizedWorkerId =
    normalizeWorker360Value(workerId);

  if (!normalizedWorkerId) return false;

  const assignedWorkerId = normalizeWorker360Value(
    shift?.assignedWorkerUid ||
      shift?.workerUid ||
      shift?.workerId ||
      shift?.assignedTo
  );

  const assignedWorkerIds = [
    ...(Array.isArray(shift?.assignedWorkerUids)
      ? shift.assignedWorkerUids
      : []),
    ...(Array.isArray(shift?.workerUids)
      ? shift.workerUids
      : []),
  ].map(normalizeWorker360Value);

  return (
    assignedWorkerId === normalizedWorkerId ||
    assignedWorkerIds.includes(normalizedWorkerId)
  );
}

export function getWorker360Shifts(worker, shifts = []) {
  const workerId = normalizeWorker360Value(
    worker?.uid || worker?.id
  );

  if (!workerId || !Array.isArray(shifts)) return [];

  return shifts
    .filter((shift) =>
      shiftBelongsToWorker(shift, workerId)
    )
    .sort((a, b) => {
      const aTime =
        getWorker360ShiftStart(a)?.getTime() || 0;

      const bTime =
        getWorker360ShiftStart(b)?.getTime() || 0;

      return bTime - aTime;
    });
}

export function isWorkerCancellation(
  shift,
  workerId
) {
  const status = normalizeWorker360Value(
    shift?.status
  );

  const cancelledBy = normalizeWorker360Value(
    shift?.cancelledByUid ||
      shift?.canceledByUid ||
      shift?.cancelledBy ||
      shift?.canceledBy
  );

  const source = normalizeWorker360Value(
    shift?.cancellationSource ||
      shift?.cancelSource ||
      shift?.cancelledByRole
  );

  const normalizedWorkerId =
    normalizeWorker360Value(workerId);

  return (
    status === "worker_cancelled" ||
    status === "worker_canceled" ||
    status === "cancelled_by_worker" ||
    status === "canceled_by_worker" ||
    (["cancelled", "canceled"].includes(status) &&
      (cancelledBy === normalizedWorkerId ||
        source === "worker"))
  );
}

export function isWorkerLateArrival(
  shift,
  graceMinutes = 15
) {
  const start = getWorker360ShiftStart(shift);

  const arrived = worker360ToDate(
    shift?.arrivedAt ||
      shift?.arrivalTime ||
      shift?.checkedInAt
  );

  if (!start || !arrived) return false;

  const minutesLate =
    (arrived.getTime() - start.getTime()) / 60000;

  return minutesLate > graceMinutes;
}

export function buildWorker360Metrics(
  worker,
  shifts = []
) {
  const workerId = normalizeWorker360Value(
    worker?.uid || worker?.id
  );

  const workerShifts = getWorker360Shifts(
    worker,
    shifts
  );

  const completedShifts = workerShifts.filter(
    (shift) =>
      normalizeWorker360Value(shift?.status) ===
      "completed"
  );

  const activeStatuses = [
    "assigned",
    "accepted",
    "activated",
    "tracking",
    "arrived",
  ];

  const activeShifts = workerShifts.filter((shift) =>
    activeStatuses.includes(
      normalizeWorker360Value(shift?.status)
    )
  );

  const noShowShifts = workerShifts.filter((shift) =>
    ["no_show", "no-show", "noshow"].includes(
      normalizeWorker360Value(shift?.status)
    )
  );

  const workerCancelledShifts =
    workerShifts.filter((shift) =>
      isWorkerCancellation(shift, workerId)
    );

  const lateArrivalShifts =
    completedShifts.filter((shift) =>
      isWorkerLateArrival(shift)
    );

  const totalHours = completedShifts.reduce(
    (total, shift) =>
      total + getWorker360ShiftHours(shift),
    0
  );

  const facilityIds = new Set(
    workerShifts
      .map((shift) =>
        normalizeWorker360Value(shift?.facilityId)
      )
      .filter(Boolean)
  );

  const attendanceOutcomes =
    completedShifts.length + noShowShifts.length;

  const reliabilityOutcomes =
    completedShifts.length +
    noShowShifts.length +
    workerCancelledShifts.length;

  const attendance =
    attendanceOutcomes > 0
      ? Math.round(
          (completedShifts.length /
            attendanceOutcomes) *
            100
        )
      : null;

  const reliability =
    reliabilityOutcomes > 0
      ? Math.max(
          0,
          Math.round(
            ((completedShifts.length -
              lateArrivalShifts.length * 0.25) /
              reliabilityOutcomes) *
              100
          )
        )
      : null;

  return {
    workerShifts,
    completedShifts,
    activeShifts,
    noShowShifts,
    workerCancelledShifts,
    lateArrivalShifts,
    completedCount: completedShifts.length,
    activeCount: activeShifts.length,
    noShowCount: noShowShifts.length,
    workerCancellationCount:
      workerCancelledShifts.length,
    lateArrivalCount: lateArrivalShifts.length,
    totalHours,
    facilitiesWorked: facilityIds.size,
    attendance,
    reliability,
  };
}